'use client';
import Link from 'next/link';
import clsx from 'clsx';
import { categoryDescription, categoryDisplayName } from '@utils/category-name';
import { assignTiles, fitTileFont } from '@lib/tile-font';
import { trackEvent } from '../../../lib/analytics/posthog';
import { EVENTS } from '../../../lib/analytics/events';

const FALLBACK = [
  { keyword: 'Pestañas', slug: 'pestanas' },
  { keyword: 'Uñas', slug: 'unas' },
  { keyword: 'Peluquería', slug: 'peluqueria' },
  { keyword: 'Barbería', slug: 'barberia' },
  { keyword: 'Centro de estética', slug: 'centro-de-estetica' },
  { keyword: 'Cejas', slug: 'cejas' }
];

// Póster tipográfico: nueve rubros en una grilla de 4 columnas (en el teléfono, cinco en 2 columnas)
// y una baldosa «Ver todas». Sin fotos: ningún negocio representa a un rubro. Cada baldosa tiene su
// medida de escritorio (`d`) y de teléfono (`m`, `null` = no se muestra); el cuerpo de letra sale de
// esas medidas y del nombre real, para que «Centro de estética» entre donde entraba «Pestañas».
const SLOTS = [
  { tone: 'ink', d: { w: 601, h: 230, max: 96 }, m: { w: 358, h: 170, max: 62 }, wide: true },
  { tone: 'blue', d: { w: 293.5, h: 474, max: 80 }, m: { w: 174, h: 310, max: 46 }, tall: true },
  { tone: 'mist', d: { w: 293.5, h: 230, max: 58 }, m: { w: 174, h: 150, max: 36 } },
  { tone: 'line', d: { w: 293.5, h: 230, max: 52 }, m: { w: 174, h: 150, max: 30 } },
  { tone: 'line', d: { w: 293.5, h: 230, max: 44 }, m: { w: 174, h: 84, max: 26, plain: true } },
  { tone: 'mist', d: { w: 293.5, h: 230, max: 58 }, m: null },
  { tone: 'line', d: { w: 293.5, h: 150, max: 30, plain: true }, m: null },
  { tone: 'line', d: { w: 293.5, h: 150, max: 30, plain: true }, m: null },
  { tone: 'line', d: { w: 293.5, h: 150, max: 30, plain: true }, m: null }
];

const desktopSlot = (slot) => ({ ...slot.d, pad: 28, go: !slot.d.plain, desc: !slot.d.plain });

export default function Categories({ categories }) {
  const top = (categories?.length > 0 ? categories : FALLBACK).slice(0, SLOTS.length).map((c) => ({
    name: categoryDisplayName(c.keyword),
    slug: c.slug,
    description: categoryDescription(c.slug)
  }));
  // Los rubros más buscados, cada uno en la baldosa donde su nombre luce más grande.
  const list = assignTiles(
    top.map((c) => c.name),
    SLOTS.slice(0, top.length).map(desktopSlot)
  )
    .filter((j) => j >= 0)
    .map((j) => top[j]);

  const track = (cat, position) =>
    trackEvent(EVENTS.HOME_CATEGORY_CARD_CLICKED, { category: cat.name, category_slug: cat.slug, position });

  return (
    <section className="home-section" aria-labelledby="categories-title">
      <div className="home-wrap">
        <div className="home-head">
          <h2 id="categories-title" className="home-title font-display">
            ¿Qué te querés
            <br className="hidden md:inline" /> hacer?
          </h2>
          <Link href="/directorio" className="home-link hidden md:inline-flex">
            Todas las categorías →
          </Link>
        </div>
        <div className="cats">
          {list.map((cat, i) => {
            const slot = SLOTS[i];
            const style = {
              '--fs': fitTileFont(cat.name, desktopSlot(slot)),
              // La flecha mide 40px en 1440 y escala con la baldosa, igual que la letra.
              '--go': Math.round((40 / (slot.d.w - 56)) * 10000) / 100,
              '--fs-m': slot.m ? fitTileFont(cat.name, { ...slot.m, pad: 18, go: false, desc: !slot.m.plain }) : 0
            };
            return (
              <Link
                key={cat.slug}
                href={`/${cat.slug}`}
                style={style}
                className={clsx('cat', `cat--${slot.tone}`, {
                  'cat--wide': slot.wide,
                  'cat--tall': slot.tall,
                  'cat--plain': slot.d.plain,
                  'cat--plain-m': slot.m?.plain,
                  'cat--desktop': !slot.m
                })}
                onClick={() => track(cat, i)}
              >
                <span className="cat__top">
                  {!slot.d.plain && (
                    <span className="cat__go" aria-hidden="true">
                      →
                    </span>
                  )}
                  <span className="cat__name font-display">{cat.name}</span>
                </span>
                {!slot.d.plain && cat.description && <span className="cat__desc">{cat.description}</span>}
              </Link>
            );
          })}
          <Link href="/directorio" className="cat cat--ink cat--all" onClick={() => track({ name: 'Ver todas', slug: 'directorio' }, list.length)}>
            <span className="cat__name font-display">Ver todas →</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
